import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { shouldForceLogoutOn401 } from '@/config/auth-failure-policy';
import { userIamUrl } from '@/config/user-iam';
import { fetchActiveBreak } from '@/services/break/breakApi';
import { fetchCrmUserProfile, normalizeCrmUserProfile } from '@/shared/services/crm-user-profile';
import { resolveProfilePhonePauseFields, withResolvedProfilePhonePause } from '@/shared/model/phone-pause';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from '@/shared/reducers/reducer.utils';
import { normalizeAccountToSession, getAccountRoleNames } from './account-mapper';
import { createNoAssignedRolesError, isNoAssignedRolesError, NO_ASSIGNED_ROLES_CODE } from './account-errors';
import { DEMO_LOGIN_ENABLED, DEMO_PASSWORD, DEMO_USERNAME, clearStoredAuth, clearStoredSessionSnapshot, createDemoAdminSession, getStoredAuth, setStoredAuth, } from './auth-storage';
import { getAccessToken, getRefreshToken, storeTokensFromAuthResponse } from './jwt-storage';
const LOGIN_URL = () => userIamUrl('/user/api/authenticate');
const ACCOUNT_URL = () => userIamUrl('/user/api/account');
const REFRESH_URL = () => userIamUrl('/user/api/app/refresh-token');
const LOGOUT_URL = () => userIamUrl('/user/api/logout');
/**
 * POST `user/api/logout` with an empty body and `Accept` set to accept any content type (IAM / NaqdPay backend).
 * Uses configured axios interceptors so Bearer is sent when a token exists.
 */
async function logoutOnServer() {
    await axios.request({
        method: 'POST',
        url: LOGOUT_URL(),
        headers: {
            Accept: '*/*',
        },
    });
}
/** Invalidate session on IAM when possible; always clear local tokens and cached session snapshot. */
async function logoutAndClearClient() {
    try {
        await logoutOnServer();
    }
    catch {
        /* offline or token already invalid — still clear client state */
    }
    clearStoredAuth();
}
async function authenticate(username, password, rememberMe = false) {
    const res = await axios.post(LOGIN_URL(), {
        username: username.trim(),
        password,
        rememberMe,
    });
    storeTokensFromAuthResponse(res.data ?? {});
}
async function fetchAccountSession() {
    const res = await axios.get(ACCOUNT_URL());
    const data = (res?.data ?? {});
    /** Monolithic gates on `activated` only when explicitly false — missing means treat as usable. */
    if (data.activated === false) {
        throw Object.assign(new Error('Account inactive'), { response: { status: 403 } });
    }
    if (getAccountRoleNames(data).length === 0) {
        throw createNoAssignedRolesError();
    }
    return normalizeAccountToSession(data);
}
/**
 * Persist tokens returned by IAM refresh-token (same nesting as Monolithic refreshToken thunk).
 */
function applyRefreshResponseBody(body) {
    const root = body;
    const mid = root?.data;
    const inner = mid && typeof mid === 'object' && 'data' in mid ? mid.data : mid;
    const tokens = inner && typeof inner === 'object' ? inner : body;
    storeTokensFromAuthResponse(tokens);
}
async function refreshAccessTokenSilent() {
    const refresh_token = getRefreshToken();
    if (!refresh_token)
        return;
    const res = await axios.post(REFRESH_URL(), {
        refresh_token,
    });
    applyRefreshResponseBody(res.data ?? {});
}
async function loadCrmProfileAndActiveBreak() {
    let crmProfile = null;
    let activeBreak = null;
    try {
        crmProfile = await fetchCrmUserProfile();
    }
    catch {
        /* CRM profile optional — softphone falls back to static defaults */
    }
    try {
        activeBreak = await fetchActiveBreak();
    }
    catch {
        /* active break optional — toolbar shows idle until next poll */
    }
    return { crmProfile, activeBreak };
}
async function loginWithAccount(username, password, rememberMe) {
    await authenticate(username, password, rememberMe ?? false);
    const session = await fetchAccountSession();
    setStoredAuth(session);
    const { crmProfile, activeBreak } = await loadCrmProfileAndActiveBreak();
    try {
        if (typeof rememberMe === 'boolean' && typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('loginRememberMe', rememberMe ? '1' : '0');
        }
    }
    catch {
        /* ignore */
    }
    return { session, crmProfile, activeBreak };
}
function readInitialSession() {
    if (typeof window === 'undefined') {
        return null;
    }
    return getStoredAuth();
}
function storeCrmProfileFromApi(state, raw) {
    if (!raw) {
        state.crmProfile = null;
        return;
    }
    const normalized = normalizeCrmUserProfile(raw) ?? raw;
    state.crmProfile = withResolvedProfilePhonePause(normalized);
}
function mergeCrmProfilePhonePauseFromApi(state, raw) {
    if (!raw)
        return;
    const normalized = normalizeCrmUserProfile(raw) ?? raw;
    const pause = resolveProfilePhonePauseFields(normalized);
    if (state.crmProfile) {
        state.crmProfile = {
            ...state.crmProfile,
            ...pause,
        };
    }
    else {
        state.crmProfile = withResolvedProfilePhonePause(normalized);
        state.crmProfileLoaded = true;
    }
}
const initialState = {
    session: readInitialSession(),
    /** CRM `GET /api/users/profile` — softphone + activation phone fields. */
    crmProfile: null,
    crmProfileLoading: false,
    crmProfileLoaded: false,
    /** `GET /api/breaks/active` — restored on login / refresh. */
    activeBreak: null,
    activeBreakLoading: false,
    activeBreakLoaded: false,
    /** Bumped when supervisor force-service websocket clears agent pause state. */
    supervisorForceServiceRevision: 0,
    lastSupervisorForceServiceCleared: [],
    loading: false,
    hydrating: false,
    /** IAM account returned with an empty `roles` array — user must sign out. */
    noAssignedRoles: false,
    errorMessage: null,
};
/** Prefer IAM-linked `userId`; fall back to activation record `id`. */
export function resolveCrmProfileUserId(profile) {
    const userId = profile?.userId ?? profile?.id;
    return userId != null && Number.isFinite(userId) ? userId : undefined;
}
/** CRM user activation record `id` — used for agent assignment (`selectedAgentIds`). */
export function resolveCrmProfileActivationId(profile) {
    const id = profile?.id;
    return id != null && Number.isFinite(id) ? id : undefined;
}
export function resolveCrmProfileUsername(profile) {
    const username = profile?.username?.trim();
    return username || undefined;
}
/** Load CRM profile + active break for the authenticated user (toolbar / softphone context). */
/** Refresh CRM profile + active break (e.g. back-forward cache restore). */
export const refreshCrmProfileSession = createAsyncThunk('auth/refreshCrmProfileSession', async () => loadCrmProfileAndActiveBreak(), { serializeError: serializeAxiosError });
export const loadCrmProfile = createAsyncThunk('auth/loadCrmProfile', async () => loadCrmProfileAndActiveBreak(), {
    serializeError: serializeAxiosError,
    condition: (_, { getState }) => {
        const auth = getState().auth;
        return !auth.crmProfileLoading && !auth.crmProfileLoaded && !auth.hydrating;
    },
});
/**
 * Refresh pause fields from `GET /api/users/profile` after hangup (PBX sets `isPhonePause` / `phonePauseEnd`).
 * Merges only ACW-related fields so SIP account deps in {@link SipCallProvider} stay stable.
 */
export const refreshCrmProfilePhonePause = createAsyncThunk('auth/refreshCrmProfilePhonePause', async () => fetchCrmUserProfile(), { serializeError: serializeAxiosError });
/** Refresh only `GET /api/breaks/active` (e.g. after starting break). */
export const loadActiveBreak = createAsyncThunk('auth/loadActiveBreak', async () => fetchActiveBreak(), { serializeError: serializeAxiosError });
export const login = createAsyncThunk('auth/login', async ({ username, password, rememberMe }) => {
    const trimmed = username.trim();
    if (DEMO_LOGIN_ENABLED && trimmed === DEMO_USERNAME && password === DEMO_PASSWORD) {
        const session = createDemoAdminSession();
        setStoredAuth(session);
        const { crmProfile, activeBreak } = await loadCrmProfileAndActiveBreak();
        return { session, crmProfile, activeBreak };
    }
    return loginWithAccount(trimmed, password, rememberMe);
}, { serializeError: serializeAxiosError });
export const logout = createAsyncThunk('auth/logout', async () => {
    await logoutAndClearClient();
}, { serializeError: serializeAxiosError });
/** Load account when JWT exists but session snapshot is missing (page refresh). */
export const hydrateSession = createAsyncThunk('auth/hydrate', async () => {
    if (!getAccessToken()) {
        clearStoredSessionSnapshot();
        throw Object.assign(new Error('Not authenticated'), { code: 'NO_TOKEN' });
    }
    try {
        const session = await fetchAccountSession();
        setStoredAuth(session);
        const { crmProfile, activeBreak } = await loadCrmProfileAndActiveBreak();
        return { session, crmProfile, activeBreak };
    }
    catch (err) {
        if (isNoAssignedRolesError(err)) {
            clearStoredSessionSnapshot();
            throw err;
        }
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 401 && shouldForceLogoutOn401(ACCOUNT_URL())) {
            await logoutAndClearClient();
        }
        throw err;
    }
}, {
    serializeError: serializeAxiosError,
    condition: (_, { getState }) => {
        const auth = getState().auth;
        return !auth.hydrating && !(auth.session && auth.crmProfileLoaded);
    },
});
export const refreshAccessToken = createAsyncThunk('auth/refresh', async () => {
    await refreshAccessTokenSilent();
}, { serializeError: serializeAxiosError });
export const authSlice = createSlice({
    name: 'auth',
    initialState: initialState,
    reducers: {
        clearAuthState(state) {
            state.session = null;
            state.crmProfile = null;
            state.crmProfileLoading = false;
            state.crmProfileLoaded = false;
            state.activeBreak = null;
            state.activeBreakLoading = false;
            state.activeBreakLoaded = false;
            state.supervisorForceServiceRevision = 0;
            state.lastSupervisorForceServiceCleared = [];
            state.loading = false;
            state.hydrating = false;
            state.noAssignedRoles = false;
            state.errorMessage = null;
        },
        setSession(state, action) {
            state.session = action.payload;
            state.errorMessage = null;
        },
        reset() {
            return initialState;
        },
        /** Clear ACW flags locally after `PUT /api/phone-pause/clear` — avoids refetching profile (SIP reconnect). */
        clearCrmPhonePause(state) {
            if (!state.crmProfile)
                return;
            state.crmProfile = {
                ...state.crmProfile,
                isPhonePause: false,
                phonePauseEnd: undefined,
            };
        },
        setActiveBreak(state, action) {
            state.activeBreak = action.payload;
            state.activeBreakLoaded = true;
        },
        clearActiveBreak(state) {
            state.activeBreak = null;
            state.activeBreakLoaded = true;
        },
        applySupervisorForceService(state, action) {
            const cleared = action.payload.clearedPauseTypes;
            if (cleared.includes('BREAK')) {
                state.activeBreak = null;
                state.activeBreakLoaded = true;
            }
            if (cleared.includes('ACW') && state.crmProfile) {
                state.crmProfile = {
                    ...state.crmProfile,
                    isPhonePause: false,
                    phonePauseEnd: undefined,
                };
            }
            state.lastSupervisorForceServiceCleared = cleared;
            state.supervisorForceServiceRevision += 1;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(login.fulfilled, (state, action) => {
            state.loading = false;
            state.session = action.payload.session;
            storeCrmProfileFromApi(state, action.payload.crmProfile);
            state.crmProfileLoaded = true;
            state.activeBreak = action.payload.activeBreak;
            state.activeBreakLoaded = true;
            state.noAssignedRoles = false;
            state.errorMessage = null;
        })
            .addCase(login.rejected, (state, action) => {
            state.loading = false;
            state.session = null;
            state.crmProfile = null;
            state.crmProfileLoading = false;
            state.crmProfileLoaded = false;
            state.activeBreak = null;
            state.activeBreakLoaded = false;
            state.noAssignedRoles = action.error.code === NO_ASSIGNED_ROLES_CODE;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addCase(loadCrmProfile.pending, (state) => {
            state.crmProfileLoading = true;
            state.activeBreakLoading = true;
        })
            .addCase(loadCrmProfile.fulfilled, (state, action) => {
            state.crmProfileLoading = false;
            state.crmProfileLoaded = true;
            storeCrmProfileFromApi(state, action.payload.crmProfile);
            state.activeBreak = action.payload.activeBreak;
            state.activeBreakLoading = false;
            state.activeBreakLoaded = true;
        })
            .addCase(loadCrmProfile.rejected, (state) => {
            state.crmProfileLoading = false;
            state.crmProfileLoaded = true;
            state.activeBreakLoading = false;
            state.activeBreakLoaded = true;
        })
            .addCase(refreshCrmProfileSession.pending, (state) => {
            state.crmProfileLoading = true;
            state.activeBreakLoading = true;
        })
            .addCase(refreshCrmProfileSession.fulfilled, (state, action) => {
            state.crmProfileLoading = false;
            state.crmProfileLoaded = true;
            storeCrmProfileFromApi(state, action.payload.crmProfile);
            state.activeBreak = action.payload.activeBreak;
            state.activeBreakLoading = false;
            state.activeBreakLoaded = true;
        })
            .addCase(refreshCrmProfileSession.rejected, (state) => {
            state.crmProfileLoading = false;
            state.activeBreakLoading = false;
        })
            .addCase(refreshCrmProfilePhonePause.fulfilled, (state, action) => {
            mergeCrmProfilePhonePauseFromApi(state, action.payload);
        })
            .addCase(loadActiveBreak.pending, (state) => {
            state.activeBreakLoading = true;
        })
            .addCase(loadActiveBreak.fulfilled, (state, action) => {
            state.activeBreakLoading = false;
            state.activeBreakLoaded = true;
            state.activeBreak = action.payload;
        })
            .addCase(loadActiveBreak.rejected, (state) => {
            state.activeBreakLoading = false;
            state.activeBreakLoaded = true;
        })
            .addCase(logout.fulfilled, (state) => {
            state.loading = false;
            state.session = null;
            state.crmProfile = null;
            state.crmProfileLoading = false;
            state.crmProfileLoaded = false;
            state.activeBreak = null;
            state.activeBreakLoaded = false;
            state.hydrating = false;
            state.noAssignedRoles = false;
            state.errorMessage = null;
        })
            .addCase(hydrateSession.fulfilled, (state, action) => {
            state.hydrating = false;
            state.session = action.payload.session;
            storeCrmProfileFromApi(state, action.payload.crmProfile);
            state.crmProfileLoaded = true;
            state.activeBreak = action.payload.activeBreak;
            state.activeBreakLoaded = true;
            state.noAssignedRoles = false;
            state.errorMessage = null;
        })
            .addCase(hydrateSession.rejected, (state, action) => {
            state.hydrating = false;
            state.session = null;
            state.crmProfile = null;
            state.crmProfileLoading = false;
            state.crmProfileLoaded = false;
            state.activeBreak = null;
            state.activeBreakLoaded = false;
            state.noAssignedRoles = action.error.code === NO_ASSIGNED_ROLES_CODE;
        })
            .addMatcher(isPending(login), (state) => {
            state.loading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(logout), (state) => {
            state.loading = true;
        })
            .addMatcher(isPending(hydrateSession), (state) => {
            state.hydrating = true;
            state.errorMessage = null;
        })
            .addMatcher(isRejected(logout), (state) => {
            state.loading = false;
            state.session = null;
            state.crmProfile = null;
            state.crmProfileLoading = false;
            state.crmProfileLoaded = false;
            state.activeBreak = null;
            state.activeBreakLoaded = false;
            state.noAssignedRoles = false;
        });
    },
});
export const { clearAuthState, setSession, reset, clearCrmPhonePause, setActiveBreak, clearActiveBreak, applySupervisorForceService, } = authSlice.actions;
export default authSlice.reducer;
